import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, Star, Calendar, MapPin, Clock, ShieldCheck, Zap, Lock, Sun, Moon, CircleDot, ArrowUpRight, FileText, Heart, Check, CreditCard, RotateCcw, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { BetaReportPreview } from '../components/reports/BetaReportPreview';
import { PremiumReportPreview } from '../components/reports/PremiumReportPreview';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../i18n/LanguageContext';
import { AdSlot } from '../components/ads/AdSlot';
interface FreeChartPageProps {
  onNavigate: (page: string) => void;
}
export function FreeChartPage({
  onNavigate
}: FreeChartPageProps) {
  const {
    t
  } = useLanguage();
  const [step, setStep] = useState(1);
  const [selectedService, setSelectedService] = useState('natal');
  const [selectedPackage, setSelectedPackage] = useState<'free' | 'pro' | 'package'>('free');
  const [selectedConsultationTier, setSelectedConsultationTier] = useState<'short' | 'standard' | 'extended'>('standard');
  const [selectedBundleSize, setSelectedBundleSize] = useState<20 | 50 | 100>(50);
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationComplete, setCalculationComplete] = useState(false);
  const {
    addItem
  } = useCart();
  const [formData, setFormData] = useState({
    p1: {
      name: '',
      date: '',
      time: '',
      city: ''
    },
    p2: {
      name: '',
      date: '',
      time: '',
      city: ''
    },
    year: new Date().getFullYear().toString(),
    period: '3'
  });
  const SERVICES = [{
    id: 'natal',
    title: t('services.natal'),
    desc: t('services.natal.desc'),
    icon: Star,
    color: 'text-indigo-500',
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    activeBg: 'bg-indigo-500/10',
    activeBorder: 'border-indigo-500',
    price: 3900
  }, {
    id: 'solar',
    title: t('services.solar'),
    desc: t('services.solar.desc'),
    icon: Sun,
    color: 'text-amber-500',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    activeBg: 'bg-amber-500/10',
    activeBorder: 'border-amber-500',
    price: 4400
  }, {
    id: 'compatibility',
    title: t('services.compatibility'),
    desc: t('services.compatibility.desc'),
    icon: Heart,
    color: 'text-rose-500',
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    activeBg: 'bg-rose-500/10',
    activeBorder: 'border-rose-500',
    price: 4900
  }, {
    id: 'combo',
    title: t('services.combo'),
    desc: t('services.combo.desc'),
    icon: FileText,
    color: 'text-slate-500',
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    activeBg: 'bg-slate-500/10',
    activeBorder: 'border-slate-500',
    price: 6900
  }, {
    id: 'forecast',
    title: t('services.forecast'),
    desc: t('services.forecast.desc'),
    icon: Calendar,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    activeBg: 'bg-emerald-500/10',
    activeBorder: 'border-emerald-500',
    price: 3900
  }, {
    id: 'consultation',
    title: t('services.consultation'),
    desc: t('services.consultation.desc'),
    icon: MessageCircle,
    color: 'text-sky-500',
    bg: 'bg-sky-50',
    border: 'border-sky-200',
    activeBg: 'bg-sky-500/10',
    activeBorder: 'border-sky-500',
    price: 4990
  }];
  const CONSULTATION_TIERS = [{
    id: 'short' as const,
    title: t('chart.consult.short.title'),
    desc: t('chart.consult.short.desc'),
    price: 2990,
    badge: t('chart.consult.short.badge'),
    badgeVariant: 'neutral' as const
  }, {
    id: 'standard' as const,
    title: t('chart.consult.standard.title'),
    desc: t('chart.consult.standard.desc'),
    price: 4990,
    badge: t('chart.consult.standard.badge'),
    badgeVariant: 'pro' as const,
    popular: true
  }, {
    id: 'extended' as const,
    title: t('chart.consult.extended.title'),
    desc: t('chart.consult.extended.desc'),
    price: 7990,
    badge: t('chart.consult.extended.badge'),
    badgeVariant: 'success' as const
  }];
  const STEPS = [{
    id: 1,
    title: t('chart.step.service')
  }, {
    id: 2,
    title: t('chart.step.package')
  }, {
    id: 3,
    title: t('chart.step.data')
  }, {
    id: 4,
    title: t('chart.step.result')
  }];
  // Scroll to top on step change
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [step]);
  const handleNext = () => {
    if (step < 4) setStep(step + 1);
  };
  const handlePrev = () => {
    if (step > 1) setStep(step - 1);
  };
  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedService === 'consultation') {
      const tier = CONSULTATION_TIERS.find(t => t.id === selectedConsultationTier) || CONSULTATION_TIERS[1];
      addItem({
        id: `consultation-${selectedConsultationTier}-${Date.now()}`,
        name: tier.title,
        description: `${tier.desc} • Живой разбор с профессиональным астрологом`,
        price: tier.price,
        type: 'consultation',
        format: 'Видео / Аудио',
        duration: tier.desc.split('•')[0].trim(),
        badges: [{
          label: 'Консультация',
          variant: 'pro'
        }]
      });
      onNavigate('checkout');
      return;
    }
    if (selectedPackage === 'package') {
      const bundlePrice = selectedBundleSize === 20 ? 19900 : selectedBundleSize === 50 ? 44900 : 79900;
      addItem({
        id: `pkg-${selectedBundleSize}-${Date.now()}`,
        name: `Пакет ${selectedBundleSize} отчётов`,
        description: `${selectedBundleSize} Premium-отчётов для любых услуг платформы`,
        price: bundlePrice,
        type: 'package',
        format: 'PDF-отчёты',
        duration: 'Бессрочно',
        badges: [{
          label: 'Пакет отчётов',
          variant: 'success'
        }]
      });
      onNavigate('checkout');
      return;
    }
    setStep(4);
    setIsCalculating(true);
    setCalculationComplete(false);
    // Simulate calculation delay
    setTimeout(() => {
      setIsCalculating(false);
      setCalculationComplete(true);
    }, 2000);
  };
  const resetWizard = () => {
    setStep(1);
    setCalculationComplete(false);
    setIsCalculating(false);
  };
  const currentService = SERVICES.find(s => s.id === selectedService) || SERVICES[0];
  // Mock planet data for the results table
  const planetPositions = [{
    name: t('chart.planet.sun'),
    icon: <Sun className="w-4 h-4 text-amber-500" data-id="element-19322" />,
    sign: t('chart.sign.aries'),
    degree: "15° 24'"
  }, {
    name: t('chart.planet.moon'),
    icon: <Moon className="w-4 h-4 text-slate-400" data-id="element-19323" />,
    sign: t('chart.sign.taurus'),
    degree: "03° 12'"
  }, {
    name: t('chart.planet.mercury'),
    icon: <CircleDot className="w-4 h-4 text-emerald-500" data-id="element-19324" />,
    sign: t('chart.sign.pisces'),
    degree: "28° 45'"
  }, {
    name: t('chart.planet.venus'),
    icon: <Star className="w-4 h-4 text-rose-500" data-id="element-19325" />,
    sign: t('chart.sign.taurus'),
    degree: "10° 05'"
  }, {
    name: t('chart.planet.mars'),
    icon: <ArrowUpRight className="w-4 h-4 text-red-500" data-id="element-19326" />,
    sign: t('chart.sign.gemini'),
    degree: "05° 30'"
  }, {
    name: t('chart.planet.jupiter'),
    icon: <Star className="w-4 h-4 text-indigo-500" data-id="element-19327" />,
    sign: t('chart.sign.sagittarius'),
    degree: "22° 15'"
  }, {
    name: t('chart.planet.saturn'),
    icon: <Star className="w-4 h-4 text-slate-600" data-id="element-19328" />,
    sign: t('chart.sign.capricorn'),
    degree: "14° 50'"
  }];
  const renderStepper = () => <div className="mb-8" data-id="element-19329">
      <div className="flex items-center justify-between relative" data-id="element-19330">
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-1 bg-slate-100 rounded-full z-0" data-id="element-19331"></div>
        <div className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-indigo-500 rounded-full z-0 transition-all duration-300" style={{
        width: `${(step - 1) / (STEPS.length - 1) * 100}%`
      }} data-id="element-19332"></div>

        {STEPS.map(s => {
        const isActive = s.id === step;
        const isCompleted = s.id < step;
        return <div key={s.id} className="relative z-10 flex flex-col items-center" data-id="element-19333">
              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${isActive ? 'bg-indigo-600 text-white shadow-md ring-4 ring-indigo-50' : isCompleted ? 'bg-indigo-500 text-white' : 'bg-white text-slate-400 border-2 border-slate-200'}`} data-id="element-19334">
                {isCompleted ? <Check className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-19335" /> : s.id}
              </div>
              <span className={`absolute top-full mt-2 text-[10px] sm:text-xs font-medium whitespace-nowrap ${isActive ? 'text-indigo-900' : isCompleted ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-19336">
                {s.title}
              </span>
            </div>;
      })}
      </div>
    </div>;
  const renderStep1 = () => <motion.div key="step1" initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -20
  }} className="space-y-6" data-id="element-19337">
      <div className="text-center mb-6" data-id="element-19338">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-id="element-19339">
          {t('chart.step1.title')}
        </h2>
        <p className="text-slate-500 text-sm sm:text-base" data-id="element-19340">
          {t('chart.step1.subtitle')}
        </p>
      </div>

      <div className="space-y-2 max-w-2xl mx-auto" data-id="element-19341">
        {SERVICES.map(service => {
        const Icon = service.icon;
        const isActive = selectedService === service.id;
        return <button key={service.id} onClick={() => setSelectedService(service.id)} className={`flex items-center gap-3 w-full text-left px-4 py-3 rounded-xl border-2 transition-all ${isActive ? `${service.activeBorder} ${service.activeBg} shadow-sm` : 'border-slate-100 bg-white hover:border-slate-200'}`} data-id="element-19342">
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${service.bg}`} data-id="element-19343">
                <Icon className={`w-4.5 h-4.5 ${service.color}`} data-id="element-19344" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-19345">
                <div className="font-semibold text-slate-900 text-sm leading-tight flex items-center gap-2" data-id="element-19346">
                  {service.title}
                  {service.id === 'natal' && <Badge variant="pro" className="text-[9px] px-1.5 py-0" data-id="element-19347">
                      {t('chart.popular')}
                    </Badge>}
                  {service.id === 'consultation' && <Badge variant="success" className="text-[9px] px-1.5 py-0" data-id="element-19348">
                      {t('chart.new')}
                    </Badge>}
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5" data-id="element-19349">
                  {service.desc}
                </div>
              </div>
              <div className="text-right flex-shrink-0 hidden sm:block" data-id="element-19350">
                <div className="text-xs font-bold text-slate-900" data-id="element-19351">
                  {service.id === 'consultation' ? t('chart.fromPrice') : t('chart.freePremium')}
                </div>
              </div>
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors ${isActive ? `${service.activeBorder} ${service.activeBg}` : 'border-slate-300'}`} data-id="element-19352">
                {isActive && <div className={`w-2.5 h-2.5 rounded-full ${service.activeBorder.replace('border-', 'bg-')}`} data-id="element-19353" />}
              </div>
            </button>;
      })}
      </div>

      <div className="flex justify-end pt-6 border-t border-slate-100 mt-8" data-id="element-19354">
        <Button onClick={handleNext} variant="primary" icon={<ArrowRight className="w-4 h-4" data-id="element-19356" />} data-id="element-19355">
          {t('chart.btn.next')}
        </Button>
      </div>
    </motion.div>;
  const renderStep2 = () => {
    const CurrentIcon = currentService.icon;
    const isConsultation = selectedService === 'consultation';
    return <motion.div key="step2" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} className="space-y-6" data-id="element-19357">
        <div className="text-center mb-6" data-id="element-19358">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-id="element-19359">
            {isConsultation ? t('chart.step2.titleConsultation') : t('chart.step2.titleReport')}
          </h2>
          <p className="text-slate-500 text-sm sm:text-base" data-id="element-19360">
            {t('chart.step2.forService', {
            name: currentService.title
          })}
          </p>
        </div>

        <div className="max-w-2xl mx-auto space-y-6" data-id="element-19361">
          {/* Selected Service Summary */}
          <div className={`flex items-center gap-3 p-4 rounded-xl border border-slate-100 ${currentService.bg} bg-opacity-50`} data-id="element-19362">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center bg-white shadow-sm flex-shrink-0`} data-id="element-19363">
              <CurrentIcon className={`w-5 h-5 ${currentService.color}`} data-id="element-19364" />
            </div>
            <div data-id="element-19365">
              <div className="text-sm font-bold text-slate-900" data-id="element-19366">
                {currentService.title}
              </div>
              <div className="text-xs text-slate-500" data-id="element-19367">
                {currentService.desc}
              </div>
            </div>
          </div>

          {/* Tier Selection */}
          <div className="space-y-3" data-id="element-19368">
            {isConsultation ? <>
                {CONSULTATION_TIERS.map(tier => {
              const isActive = selectedConsultationTier === tier.id;
              return <button key={tier.id} onClick={() => setSelectedConsultationTier(tier.id)} className={`flex items-center gap-4 w-full text-left p-4 rounded-xl border-2 transition-all ${isActive ? 'border-sky-500 bg-sky-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-19369">
                      <div className="flex-1 min-w-0" data-id="element-19370">
                        <div className="flex items-center gap-2 mb-1" data-id="element-19371">
                          <span className="font-bold text-slate-900" data-id="element-19372">
                            {tier.title}
                          </span>
                          <Badge variant={tier.badgeVariant} className="text-[10px]" data-id="element-19373">
                            {tier.badge}
                          </Badge>
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-19374">
                          {tier.desc}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-2" data-id="element-19375">
                        <div className="text-lg font-bold text-slate-900" data-id="element-19376">
                          {tier.price.toLocaleString('ru-RU')} ₽
                        </div>
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${isActive ? 'border-sky-500 bg-sky-500' : 'border-slate-300'}`} data-id="element-19377">
                        {isActive && <div className="w-2 h-2 rounded-full bg-white" data-id="element-19378" />}
                      </div>
                    </button>;
            })}
              </> : <>
                {/* Free Tier */}
                <button onClick={() => setSelectedPackage('free')} className={`flex items-center gap-4 w-full text-left p-4 rounded-xl border-2 transition-all ${selectedPackage === 'free' ? 'border-indigo-500 bg-indigo-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-19379">
                  <div className="flex-1 min-w-0" data-id="element-19380">
                    <div className="flex items-center gap-2 mb-1" data-id="element-19381">
                      <span className="font-bold text-slate-900" data-id="element-19382">
                        {t('chart.tier.free.title')}
                      </span>
                      <Badge variant="neutral" className="text-[10px]" data-id="element-19383">
                        {t('chart.tier.free.badge')}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-19384">
                      {t('chart.tier.free.desc')}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2" data-id="element-19385">
                    <div className="text-lg font-bold text-slate-900" data-id="element-19386">0 ₽</div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${selectedPackage === 'free' ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'}`} data-id="element-19387">
                    {selectedPackage === 'free' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-19388" />}
                  </div>
                </button>

                {/* Pro Tier */}
                <button onClick={() => setSelectedPackage('pro')} className={`flex items-center gap-4 w-full text-left p-4 rounded-xl border-2 transition-all ${selectedPackage === 'pro' ? 'border-indigo-500 bg-indigo-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-19389">
                  <div className="flex-1 min-w-0" data-id="element-19390">
                    <div className="flex items-center gap-2 mb-1" data-id="element-19391">
                      <span className="font-bold text-slate-900" data-id="element-19392">
                        {t('chart.tier.pro.title')}
                      </span>
                      <Badge variant="pro" className="text-[10px]" data-id="element-19393">
                        {t('chart.tier.pro.badge')}
                      </Badge>
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-19394">
                      {t('chart.tier.pro.desc')}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0 ml-2" data-id="element-19395">
                    <div className="text-lg font-bold text-slate-900" data-id="element-19396">
                      {currentService.price.toLocaleString('ru-RU')} ₽
                    </div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${selectedPackage === 'pro' ? 'border-indigo-500 bg-indigo-500' : 'border-slate-300'}`} data-id="element-19397">
                    {selectedPackage === 'pro' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-19398" />}
                  </div>
                </button>

                {/* Package Tier */}
                <div className={`rounded-xl border-2 transition-all overflow-hidden ${selectedPackage === 'package' ? 'border-violet-500 bg-violet-50/30 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'}`} data-id="element-19399">
                  <button onClick={() => setSelectedPackage('package')} className="flex items-center gap-4 w-full text-left p-4" data-id="element-19400">
                    <div className="flex-1 min-w-0" data-id="element-19401">
                      <div className="flex items-center gap-2 mb-1" data-id="element-19402">
                        <span className="font-bold text-slate-900" data-id="element-19403">
                          {t('chart.tier.package.title')}
                        </span>
                        <Badge variant="success" className="text-[10px]" data-id="element-19404">
                          {t('chart.tier.package.badge')}
                        </Badge>
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-19405">
                        {t('chart.tier.package.desc')}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0 ml-2" data-id="element-19406">
                      <div className="text-sm font-bold text-violet-600" data-id="element-19407">
                        {t('chart.tier.package.fromPrice')}
                      </div>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ml-2 transition-colors ${selectedPackage === 'package' ? 'border-violet-500 bg-violet-500' : 'border-slate-300'}`} data-id="element-19408">
                      {selectedPackage === 'package' && <div className="w-2 h-2 rounded-full bg-white" data-id="element-19409" />}
                    </div>
                  </button>

                  {/* Expandable Package Options */}
                  <AnimatePresence data-id="element-19410">
                    {selectedPackage === 'package' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-violet-100 bg-white/50" data-id="element-19411">
                        <div className="p-4 space-y-2" data-id="element-19412">
                          {[{
                      size: 20 as const,
                      price: 19900,
                      perItem: 995
                    }, {
                      size: 50 as const,
                      price: 44900,
                      perItem: 898,
                      popular: true
                    }, {
                      size: 100 as const,
                      price: 79900,
                      perItem: 799
                    }].map(bundle => <button key={bundle.size} onClick={() => setSelectedBundleSize(bundle.size)} className={`flex items-center justify-between w-full p-3 rounded-lg border transition-all ${selectedBundleSize === bundle.size ? 'border-violet-500 bg-violet-100/50' : 'border-slate-200 bg-white hover:border-violet-300'}`} data-id="element-19413">
                              <div className="flex items-center gap-3" data-id="element-19414">
                                <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${selectedBundleSize === bundle.size ? 'border-violet-500' : 'border-slate-300'}`} data-id="element-19415">
                                  {selectedBundleSize === bundle.size && <div className="w-2 h-2 rounded-full bg-violet-500" data-id="element-19416" />}
                                </div>
                                <div className="flex items-center gap-2" data-id="element-19417">
                                  <span className="font-bold text-slate-900 text-sm" data-id="element-19418">
                                    {t('chart.bundle.reports', {
                              count: bundle.size.toString()
                            })}
                                  </span>
                                  {bundle.popular && <span className="text-[9px] font-bold text-white bg-violet-500 px-1.5 py-0.5 rounded-full" data-id="element-19419">
                                      {t('chart.bundle.hit')}
                                    </span>}
                                </div>
                              </div>
                              <div className="text-right" data-id="element-19420">
                                <div className="text-sm font-bold text-slate-900" data-id="element-19421">
                                  {bundle.price.toLocaleString('ru-RU')} ₽
                                </div>
                                <div className="text-[10px] text-violet-600" data-id="element-19422">
                                  {bundle.perItem.toLocaleString('ru-RU')} ₽/шт
                                </div>
                              </div>
                            </button>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </>}
          </div>
        </div>

        <div className="flex justify-between pt-6 border-t border-slate-100 mt-8 max-w-2xl mx-auto" data-id="element-19423">
          <Button onClick={handlePrev} variant="outline" icon={<ArrowLeft className="w-4 h-4" data-id="element-19425" />} data-id="element-19424">
            {t('chart.btn.back')}
          </Button>
          <Button onClick={handleNext} variant="primary" icon={<ArrowRight className="w-4 h-4" data-id="element-19427" />} data-id="element-19426">
            {t('chart.btn.next')}
          </Button>
        </div>
      </motion.div>;
  };
  const renderPersonFields = (personKey: 'p1' | 'p2', title?: string) => <div className="space-y-4 bg-slate-50 p-4 sm:p-6 rounded-2xl border border-slate-100" data-id="element-19428">
      {title && <h3 className="font-bold text-slate-900 mb-2" data-id="element-19429">{title}</h3>}
      <Input label={t('chart.field.name')} placeholder={t('chart.field.namePlaceholder')} value={formData[personKey].name} onChange={e => setFormData({
      ...formData,
      [personKey]: {
        ...formData[personKey],
        name: e.target.value
      }
    })} data-id="element-19430" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-19431">
        <Input label={t('chart.field.date')} type="date" required value={formData[personKey].date} onChange={e => setFormData({
        ...formData,
        [personKey]: {
          ...formData[personKey],
          date: e.target.value
        }
      })} data-id="element-19432" />
        <div data-id="element-19433">
          <Input label={t('chart.field.time')} type="time" required value={formData[personKey].time} onChange={e => setFormData({
          ...formData,
          [personKey]: {
            ...formData[personKey],
            time: e.target.value
          }
        })} data-id="element-19434" />
          <p className="text-[10px] sm:text-xs text-slate-500 mt-1" data-id="element-19435">
            {t('chart.field.timeHint')}
          </p>
        </div>
      </div>
      <Input label={t('chart.field.city')} placeholder={t('chart.field.cityPlaceholder')} required icon={<MapPin className="w-4 h-4" data-id="element-19437" />} value={formData[personKey].city} onChange={e => setFormData({
      ...formData,
      [personKey]: {
        ...formData[personKey],
        city: e.target.value
      }
    })} data-id="element-19436" />
    </div>;
  const renderStep3 = () => {
    const CurrentIcon = currentService.icon;
    return <motion.div key="step3" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} className="space-y-6" data-id="element-19438">
        <div className="text-center mb-6" data-id="element-19439">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-id="element-19440">
            {t('chart.step3.title')}
          </h2>
          <p className="text-slate-500 text-sm sm:text-base" data-id="element-19441">
            {t('chart.step3.subtitle', {
            name: currentService.title
          })}
          </p>
        </div>

        <div className="max-w-2xl mx-auto mb-6" data-id="element-19442">
          <div className="flex items-center justify-between p-3 sm:p-4 rounded-xl bg-indigo-50/50 border border-indigo-100" data-id="element-19443">
            <div className="flex items-center gap-3" data-id="element-19444">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center bg-white shadow-sm flex-shrink-0 ${currentService.color}`} data-id="element-19445">
                <CurrentIcon className="w-4 h-4" data-id="element-19446" />
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2" data-id="element-19447">
                <span className="text-sm font-bold text-slate-900" data-id="element-19448">
                  {currentService.title}
                </span>
                <span className="hidden sm:inline text-slate-300" data-id="element-19449">•</span>
                <span className="text-xs font-medium text-slate-600" data-id="element-19450">
                  {selectedService === 'consultation' ? CONSULTATION_TIERS.find(t => t.id === selectedConsultationTier)?.title : selectedPackage === 'free' ? t('chart.tier.free.title') : selectedPackage === 'pro' ? t('chart.tier.pro.title') : t('chart.bundle.reports', {
                  count: selectedBundleSize.toString()
                })}
                </span>
              </div>
            </div>
            <div className="text-right" data-id="element-19451">
              <div className="text-sm font-bold text-indigo-600" data-id="element-19452">
                {selectedService === 'consultation' ? `${(CONSULTATION_TIERS.find(t => t.id === selectedConsultationTier)?.price || 4990).toLocaleString('ru-RU')} ₽` : selectedPackage === 'free' ? '0 ₽' : selectedPackage === 'pro' ? `${currentService.price.toLocaleString('ru-RU')} ₽` : `${(selectedBundleSize === 20 ? 19900 : selectedBundleSize === 50 ? 44900 : 79900).toLocaleString('ru-RU')} ₽`}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={handleCalculate} className="space-y-6 max-w-2xl mx-auto" data-id="element-19453">
          {selectedService === 'compatibility' ? <div className="space-y-6" data-id="element-19454">
              {renderPersonFields('p1', t('chart.field.person1'))}
              {renderPersonFields('p2', t('chart.field.person2'))}
            </div> : <div className="space-y-6" data-id="element-19455">
              {renderPersonFields('p1')}

              {selectedService === 'solar' && <div className="bg-amber-50/50 p-4 sm:p-6 rounded-2xl border border-amber-100" data-id="element-19456">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-19457">
                    {t('chart.field.forecastYear')}
                  </label>
                  <select className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" value={formData.year} onChange={e => setFormData({
              ...formData,
              year: e.target.value
            })} data-id="element-19458">
                    {[0, 1, 2, 3].map(offset => {
                const year = new Date().getFullYear() + offset;
                return <option key={year} value={year} data-id="element-19459">
                          {year}
                        </option>;
              })}
                  </select>
                </div>}

              {selectedService === 'forecast' && <div className="bg-emerald-50/50 p-4 sm:p-6 rounded-2xl border border-emerald-100" data-id="element-19460">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-19461">
                    {t('chart.field.forecastPeriod')}
                  </label>
                  <select className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" value={formData.period} onChange={e => setFormData({
              ...formData,
              period: e.target.value
            })} data-id="element-19462">
                    <option value="3" data-id="element-19463">{t('chart.period.3months')}</option>
                    <option value="6" data-id="element-19464">{t('chart.period.6months')}</option>
                    <option value="12" data-id="element-19465">{t('chart.period.1year')}</option>
                  </select>
                </div>}
            </div>}

          <div className="flex justify-between pt-6 border-t border-slate-100 mt-8" data-id="element-19466">
            <Button type="button" onClick={handlePrev} variant="outline" icon={<ArrowLeft className="w-4 h-4" data-id="element-19468" />} data-id="element-19467">
              {t('chart.btn.back')}
            </Button>
            <Button type="submit" variant="primary" icon={selectedService === 'consultation' ? <ArrowRight className="w-4 h-4" data-id="element-19470" /> : <Zap className="w-4 h-4" data-id="element-19471" />} data-id="element-19469">
              {selectedService === 'consultation' ? t('chart.btn.book') : t('chart.btn.calculate')}
            </Button>
          </div>
        </form>
      </motion.div>;
  };
  const renderStep4 = () => {
    if (isCalculating) {
      return <motion.div key="calculating" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} className="flex flex-col items-center justify-center py-20 text-center space-y-6" data-id="element-19472">
          <div className="relative w-24 h-24" data-id="element-19473">
            <div className="absolute inset-0 border-4 border-slate-100 rounded-full" data-id="element-19474"></div>
            <div className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent animate-spin" data-id="element-19475"></div>
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-19476">
              <currentService.icon className={`w-8 h-8 ${currentService.color}`} data-id="element-19477" />
            </div>
          </div>
          <div data-id="element-19478">
            <h2 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-19479">
              {t('chart.calculating.title')}
            </h2>
            <p className="text-slate-500" data-id="element-19480">{t('chart.calculating.subtitle')}</p>
          </div>
        </motion.div>;
    }
    return <motion.div key="result" initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-6" data-id="element-19481">
        {/* New calculation button */}
        <div className="flex items-center justify-between" data-id="element-19482">
          <div data-id="element-19483">
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19484">
              {selectedPackage === 'pro' ? t('chart.result.fullReport') : t('chart.result.basicReport')}
              : {currentService.title}
            </h2>
            <p className="text-sm text-slate-500 mt-1" data-id="element-19485">
              {formData.p1.name && `${formData.p1.name} • `}
              {formData.p1.date || '01.01.1990'} •{' '}
              {formData.p1.city || 'Москва'}
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={resetWizard} icon={<RotateCcw className="w-3.5 h-3.5" data-id="element-19487" />} data-id="element-19486">
            {t('chart.result.newCalc')}
          </Button>
        </div>

        {/* Render the appropriate report preview */}
        {selectedPackage === 'free' ? <>
            <Card padding="md" className="border-slate-200 mb-8" data-id="element-19488">
              <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-19489">
                <Star className="w-5 h-5 text-indigo-500" data-id="element-19490" />
                {t('chart.result.planets')}
              </h3>
              <div className="overflow-hidden rounded-xl border border-slate-100" data-id="element-19491">
                <table className="w-full text-sm text-left" data-id="element-19492">
                  <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-19493">
                    <tr data-id="element-19494">
                      <th className="px-4 py-3" data-id="element-19495">{t('chart.result.planet')}</th>
                      <th className="px-4 py-3" data-id="element-19496">{t('chart.result.sign')}</th>
                      <th className="px-4 py-3 text-right" data-id="element-19497">
                        {t('chart.result.degree')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-19498">
                    {planetPositions.map((planet, i) => <tr key={i} className="hover:bg-slate-50/50 transition-colors" data-id="element-19499">
                        <td className="px-4 py-3 flex items-center gap-2 font-medium text-slate-900" data-id="element-19500">
                          {planet.icon}
                          {planet.name}
                        </td>
                        <td className="px-4 py-3 text-slate-600" data-id="element-19501">
                          {planet.sign}
                        </td>
                        <td className="px-4 py-3 text-right text-slate-500 font-mono text-xs" data-id="element-19502">
                          {planet.degree}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </Card>

            <div className="flex items-center gap-4 mb-8" data-id="element-19503">
              <div className="h-px bg-slate-200 flex-1" data-id="element-19504" />
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-19505">
                {t('chart.result.briefInterpretation')}
              </span>
              <div className="h-px bg-slate-200 flex-1" data-id="element-19506" />
            </div>

            <BetaReportPreview serviceType={selectedService as 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast'} onNavigate={onNavigate} data-id="element-19507" />

            <div className="mt-8 space-y-4" data-id="element-19508">
              <Card padding="lg" className="bg-gradient-to-br from-indigo-900 to-slate-900 border-indigo-800 text-white shadow-xl shadow-indigo-900/20" data-id="element-19509">
                <div className="flex flex-col sm:flex-row items-center gap-6" data-id="element-19510">
                  <div className="flex-1 text-center sm:text-left" data-id="element-19511">
                    <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-500/20 text-indigo-300 text-xs font-bold mb-3 border border-indigo-500/30" data-id="element-19512">
                      <Sparkles className="w-3.5 h-3.5" data-id="element-19513" /> Premium
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold mb-2" data-id="element-19514">
                      {t('chart.result.wantFull')}
                    </h3>
                    <p className="text-indigo-200 text-sm leading-relaxed" data-id="element-19515">
                      {t('chart.result.fullDesc')}
                    </p>
                  </div>
                  <div className="w-full sm:w-auto flex-shrink-0" data-id="element-19516">
                    <Button variant="primary" size="lg" fullWidth className="bg-indigo-500 hover:bg-indigo-400 text-white shadow-lg shadow-indigo-500/25" onClick={() => {
                  const priceMap: Record<string, number> = {
                    natal: 3900,
                    solar: 3900,
                    compatibility: 4900,
                    combo: 6900,
                    forecast: 3900
                  };
                  addItem({
                    id: `premium-${selectedService}-${Date.now()}`,
                    name: `Premium отчёт: ${currentService.title}`,
                    description: currentService.desc,
                    price: priceMap[selectedService] || 3900,
                    type: 'report',
                    format: 'PDF-отчёт',
                    badges: [{
                      label: 'Premium',
                      variant: 'pro'
                    }]
                  });
                  onNavigate('checkout');
                }} data-id="element-19517">
                      {t('chart.result.getPremium')}
                    </Button>
                  </div>
                </div>
              </Card>

              <Card padding="md" className="border-slate-200 bg-slate-50 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-19518">
                <div className="flex items-center gap-3" data-id="element-19519">
                  <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-sky-500" data-id="element-19520">
                    <MessageCircle className="w-5 h-5" data-id="element-19521" />
                  </div>
                  <div data-id="element-19522">
                    <div className="font-bold text-slate-900 text-sm" data-id="element-19523">
                      {t('chart.result.questions')}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-19524">
                      {t('chart.result.consultDesc')}
                    </div>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="w-full sm:w-auto bg-white" onClick={() => onNavigate('service-consultation')} data-id="element-19525">
                  {t('chart.result.chooseAstrologer')}
                </Button>
              </Card>
            </div>
          </> : <PremiumReportPreview serviceType={selectedService as 'natal' | 'solar' | 'compatibility' | 'combo' | 'forecast'} data-id="element-19526" />}
      </motion.div>;
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-24" data-id="element-19527">
      <div className="bg-white border-b border-slate-200 pt-8 pb-12" data-id="element-19528">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-19529">
          <Breadcrumbs items={[{
          label: t('nav.services'),
          onClick: () => onNavigate('home')
        }, {
          label: t('chart.breadcrumb'),
          onClick: resetWizard
        }]} className="mb-8" data-id="element-19530" />

          <div className="text-center mb-10" data-id="element-19531">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 mb-4" data-id="element-19532">
              {t('chart.title')}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 max-w-2xl mx-auto mb-6" data-id="element-19533">
              {t('chart.subtitle')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6" data-id="element-19534">
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600 bg-slate-100/80 px-3 py-1.5 rounded-full" data-id="element-19535">
                <Star className="w-4 h-4 text-amber-500 fill-amber-500" data-id="element-19536" />
                {t('chart.rating')}
              </div>
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600 bg-slate-100/80 px-3 py-1.5 rounded-full" data-id="element-19537">
                <Zap className="w-4 h-4 text-indigo-500" data-id="element-19538" />
                {t('chart.calculations')}
              </div>
              <div className="flex items-center gap-1.5 text-sm font-medium text-slate-600 bg-slate-100/80 px-3 py-1.5 rounded-full" data-id="element-19539">
                <ShieldCheck className="w-4 h-4 text-emerald-500" data-id="element-19540" />
                {t('chart.freeBeta')}
              </div>
            </div>
          </div>

          {/* Stepper only visible during steps 1-3 */}
          {step < 4 && renderStepper()}
        </div>
      </div>

      <div className={`mx-auto px-4 sm:px-6 lg:px-8 mt-8 sm:mt-12 ${step === 4 ? 'max-w-7xl' : 'max-w-3xl'}`} data-id="element-19541">
        <AnimatePresence mode="wait" data-id="element-19542">
          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && <div className="space-y-8" data-id="element-19543">
              {renderStep4()}
              <AdSlot slotId="SLOT-08" onNavigate={onNavigate} data-id="element-19544" />
            </div>}
        </AnimatePresence>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-24" data-id="element-19545">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6" data-id="element-19546">
          <Card padding="md" className="bg-white border-slate-100 text-center" data-id="element-19547">
            <div className="w-12 h-12 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-4" data-id="element-19548">
              <ShieldCheck className="w-6 h-6 text-indigo-500" data-id="element-19549" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-19550">
              {t('chart.trust.accurate.title')}
            </h3>
            <p className="text-sm text-slate-500" data-id="element-19551">
              {t('chart.trust.accurate.desc')}
            </p>
          </Card>
          <Card padding="md" className="bg-white border-slate-100 text-center" data-id="element-19552">
            <div className="w-12 h-12 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4" data-id="element-19553">
              <Lock className="w-6 h-6 text-emerald-500" data-id="element-19554" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-19555">
              {t('chart.trust.privacy.title')}
            </h3>
            <p className="text-sm text-slate-500" data-id="element-19556">
              {t('chart.trust.privacy.desc')}
            </p>
          </Card>
          <Card padding="md" className="bg-white border-slate-100 text-center" data-id="element-19557">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4" data-id="element-19558">
              <Zap className="w-6 h-6 text-amber-500" data-id="element-19559" />
            </div>
            <h3 className="font-bold text-slate-900 mb-2" data-id="element-19560">
              {t('chart.trust.instant.title')}
            </h3>
            <p className="text-sm text-slate-500" data-id="element-19561">
              {t('chart.trust.instant.desc')}
            </p>
          </Card>
        </div>
      </div>
    </div>;
}